import React from "react";
import { inr } from "../../lib/api";
import ResourceManager, { Badge } from "./ResourceManager";

const EXAM_ICONS = ["ShieldCheck", "Landmark", "Banknote", "Train", "MapPin", "GraduationCap", "Crosshair", "BookOpen"].map((v) => ({ value: v, label: v }));
const PRODUCT_TYPES = ["Full Test Series", "Mock Test", "Sectional Test", "Subject Test", "Chapter Test", "PYQ Test", "MCQ Pack", "Practice Set", "Mini Test", "Free Test", "Combo Pack"].map((v) => ({ value: v, label: v }));

export function AdminExams() {
  return (
    <ResourceManager
      title="Exams" subtitle="Create, edit, duplicate and publish exam sales pages." testid="admin-exams"
      endpoint="/admin/exams" canDuplicate defaults={{ published: false, order: 100, icon: "GraduationCap" }}
      columns={[
        { key: "name", label: "Name", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-content-muted">/{r.slug}</div></div> },
        { key: "category", label: "Category" },
        { key: "published", label: "Status", render: (r) => <Badge on={r.published} /> },
        { key: "order", label: "Order" },
      ]}
      fields={[
        { name: "name", label: "Exam name", full: true },
        { name: "slug", label: "Slug (URL)" },
        { name: "category", label: "Category" },
        { name: "icon", label: "Icon", type: "select", options: EXAM_ICONS },
        { name: "hero_image", label: "Hero / banner image", type: "image", full: true },
        { name: "short_description", label: "Short description", full: true },
        { name: "description", label: "Description", type: "textarea" },
        { name: "hero_headline", label: "Hero headline", full: true },
        { name: "hero_subtext", label: "Hero subtext", type: "textarea" },
        { name: "benefits", label: "Benefits (comma separated)", type: "tags", full: true },
        { name: "languages", label: "Languages (comma separated)", type: "tags" },
        { name: "difficulty", label: "Difficulty" },
        { name: "exam_pattern", label: "Exam pattern", type: "textarea" },
        { name: "syllabus", label: "Syllabus", type: "textarea" },
        { name: "pyq_available", label: "PYQ available", type: "switch" },
        { name: "order", label: "Display order", type: "number" },
        { name: "published", label: "Published", type: "switch" },
      ]}
    />
  );
}

export function AdminProducts() {
  return (
    <ResourceManager
      title="Test Series" subtitle="Products students can purchase." testid="admin-products"
      endpoint="/admin/products" canDuplicate relations={[{ key: "exams", url: "/admin/exams" }]}
      defaults={{ published: false, featured: false, gst: 0, validity_days: 365, type: "Full Test Series", price: 0, mrp: 0 }}
      columns={[
        { key: "name", label: "Name", render: (r) => <div className="max-w-xs"><div className="font-medium truncate">{r.name}</div><div className="text-xs text-content-muted">{r.type}</div></div> },
        { key: "price", label: "Price", render: (r) => r.price === 0 ? "Free" : inr(r.price) },
        { key: "mrp", label: "MRP", render: (r) => inr(r.mrp) },
        { key: "featured", label: "Featured", render: (r) => <Badge on={r.featured} onText="Yes" offText="No" /> },
        { key: "published", label: "Status", render: (r) => <Badge on={r.published} /> },
      ]}
      fields={[
        { name: "name", label: "Product name", full: true },
        { name: "slug", label: "Slug (URL)" },
        { name: "exam_id", label: "Exam", type: "select", relation: "exams" },
        { name: "type", label: "Type", type: "select", options: PRODUCT_TYPES },
        { name: "thumbnail", label: "Thumbnail image", type: "image" },
        { name: "banner", label: "Banner image", type: "image" },
        { name: "short_description", label: "Short description", full: true },
        { name: "description", label: "Description", type: "textarea" },
        { name: "price", label: "Price (₹)", type: "number" },
        { name: "mrp", label: "MRP (₹)", type: "number" },
        { name: "gst", label: "GST %", type: "number" },
        { name: "validity_days", label: "Validity (days)", type: "number" },
        { name: "num_tests", label: "No. of tests", type: "number" },
        { name: "num_questions", label: "No. of questions", type: "number" },
        { name: "language", label: "Language" },
        { name: "difficulty", label: "Difficulty" },
        { name: "tags", label: "Tags", type: "tags" },
        { name: "featured", label: "Featured", type: "switch" },
        { name: "published", label: "Published", type: "switch" },
      ]}
    />
  );
}

export function AdminCoupons() {
  return (
    <ResourceManager
      title="Coupons" subtitle="Discount codes." testid="admin-coupons"
      endpoint="/admin/coupons" defaults={{ type: "percent", active: true, value: 10 }}
      columns={[
        { key: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
        { key: "type", label: "Type" },
        { key: "value", label: "Value", render: (r) => r.type === "percent" ? `${r.value}%` : inr(r.value) },
        { key: "used_count", label: "Used" },
        { key: "active", label: "Active", render: (r) => <Badge on={r.active} onText="Active" offText="Off" /> },
      ]}
      fields={[
        { name: "code", label: "Code" },
        { name: "type", label: "Type", type: "select", options: [{ value: "percent", label: "Percentage" }, { value: "fixed", label: "Fixed ₹" }] },
        { name: "value", label: "Value", type: "number" },
        { name: "min_order", label: "Min order (₹)", type: "number" },
        { name: "max_discount", label: "Max discount (₹)", type: "number" },
        { name: "max_usage", label: "Max usage (total)", type: "number" },
        { name: "per_user", label: "Per-user limit", type: "number" },
        { name: "first_order", label: "First order only", type: "switch" },
        { name: "active", label: "Active", type: "switch" },
      ]}
    />
  );
}

export function AdminTestimonials() {
  return (
    <ResourceManager
      title="Testimonials" testid="admin-testimonials" endpoint="/admin/testimonials"
      defaults={{ published: true, rating: 5, verified: true }}
      columns={[
        { key: "name", label: "Name" }, { key: "exam", label: "Exam" }, { key: "city", label: "City" },
        { key: "verified", label: "Verified", render: (r) => <Badge on={r.verified} onText="Verified" offText="No" /> },
        { key: "published", label: "Status", render: (r) => <Badge on={r.published} /> },
      ]}
      fields={[
        { name: "name", label: "Student name" }, { name: "exam", label: "Exam" }, { name: "city", label: "City" },
        { name: "avatar", label: "Photo", type: "image" },
        { name: "rating", label: "Rating (1-5)", type: "number" },
        { name: "text", label: "Testimonial", type: "textarea", full: true },
        { name: "verified", label: "Verified purchase", type: "switch" },
        { name: "published", label: "Published", type: "switch" },
      ]}
    />
  );
}

export function AdminFaqs() {
  return (
    <ResourceManager
      title="FAQs" testid="admin-faqs" endpoint="/admin/faqs" defaults={{ published: true, scope: "general", order: 100 }}
      columns={[
        { key: "question", label: "Question", render: (r) => <span className="max-w-md block truncate">{r.question}</span> },
        { key: "scope", label: "Scope" }, { key: "order", label: "Order" },
        { key: "published", label: "Status", render: (r) => <Badge on={r.published} /> },
      ]}
      fields={[
        { name: "question", label: "Question", full: true },
        { name: "answer", label: "Answer", type: "textarea", full: true },
        { name: "scope", label: "Scope (general or exam slug)" },
        { name: "exam_slug", label: "Exam slug (optional)" },
        { name: "order", label: "Order", type: "number" },
        { name: "published", label: "Published", type: "switch" },
      ]}
    />
  );
}

export function AdminPages() {
  return (
    <ResourceManager
      title="Custom Pages" subtitle="Create your own pages with rich text and images." testid="admin-pages"
      endpoint="/admin/pages" defaults={{ published: false, show_in_footer: false, order: 100 }}
      columns={[
        { key: "title", label: "Title", render: (r) => <div><div className="font-medium">{r.title}</div><div className="text-xs text-content-muted">/p/{r.slug}</div></div> },
        { key: "show_in_footer", label: "In footer", render: (r) => <Badge on={r.show_in_footer} onText="Yes" offText="No" /> },
        { key: "published", label: "Status", render: (r) => <Badge on={r.published} /> },
      ]}
      fields={[
        { name: "title", label: "Page title", full: true },
        { name: "slug", label: "Slug (URL) — opens at /p/your-slug" },
        { name: "cover_image", label: "Cover image", type: "image", full: true },
        { name: "content", label: "Content (rich HTML + images)", type: "html", rows: 12, full: true },
        { name: "seo_title", label: "SEO title", full: true },
        { name: "meta_description", label: "Meta description", type: "textarea", full: true },
        { name: "order", label: "Footer order", type: "number" },
        { name: "show_in_footer", label: "Show link in footer", type: "switch" },
        { name: "published", label: "Published", type: "switch" },
      ]}
    />
  );
}

export function AdminBlog() {
  return (
    <ResourceManager
      title="Blog" subtitle="Articles & CMS." testid="admin-blog" endpoint="/admin/blog"
      defaults={{ status: "draft", author: "Agni Shiksha Team" }}
      columns={[
        { key: "title", label: "Title", render: (r) => <span className="max-w-md block truncate font-medium">{r.title}</span> },
        { key: "category", label: "Category" },
        { key: "status", label: "Status", render: (r) => <Badge on={r.status === "published"} offText={r.status} /> },
      ]}
      fields={[
        { name: "title", label: "Title", full: true },
        { name: "slug", label: "Slug" },
        { name: "category", label: "Category" },
        { name: "cover_image", label: "Cover image", type: "image", full: true },
        { name: "excerpt", label: "Excerpt", type: "textarea", full: true },
        { name: "content", label: "Content (HTML)", type: "html", rows: 10, full: true },
        { name: "author", label: "Author" },
        { name: "tags", label: "Tags", type: "tags" },
        { name: "seo_title", label: "SEO title", full: true },
        { name: "meta_description", label: "Meta description", type: "textarea", full: true },
        { name: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }, { value: "archived", label: "Archived" }] },
      ]}
    />
  );
}
